import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Clock, CheckCircle, MessageSquare, Star, Loader2, FileText, X } from 'lucide-react';
import { serviceRequestAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import QuoteComparisonPanel from '../components/QuoteComparisonPanel';
import ProviderReviewForm from '../components/ProviderReviewForm';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  quoted: { label: 'Quotes Received', color: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700' },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  reviewed: { label: 'Reviewed', color: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const MyServiceRequestsPage = () => {
  const navigate = useNavigate();
  const { user, getAuthToken } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    try {
      const token = getAuthToken();
      const data = await serviceRequestAPI.getMyRequests(filter || undefined, token);
      setRequests(data);
    } catch (err) {
      console.error('Failed to load requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRequestDetail = async (requestId) => {
    setDetailLoading(true);
    try {
      const token = getAuthToken();
      const data = await serviceRequestAPI.getRequest(requestId, token);
      setSelectedRequest(data);
    } catch (err) {
      console.error('Failed to load request detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAcceptQuote = async (requestId, quoteId) => {
    try {
      const token = getAuthToken();
      await serviceRequestAPI.acceptQuote(requestId, quoteId, token);
      await loadRequestDetail(requestId);
      loadRequests();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to accept quote');
    }
  };

  // Pay the accepted quote. Creates a Stripe Checkout Session via the
  // platform and redirects the client to the hosted checkout page.
  // On return the success_url lands them back here with a paid_request_id
  // query param; for now we just rely on a subsequent reload to pick up
  // the new paymentStatus.
  const handlePayForRequest = async (requestId) => {
    try {
      const token = getAuthToken();
      const { paymentsAPI } = await import('../services/api');
      const { checkoutUrl } = await paymentsAPI.createServiceRequestCheckout(
        requestId,
        window.location.origin,
        token,
      );
      if (!checkoutUrl) {
        alert('Could not start the payment. Please try again.');
        return;
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start payment');
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this service request?')) return;
    try {
      const token = getAuthToken();
      await serviceRequestAPI.cancel(requestId, token);
      setSelectedRequest(null);
      loadRequests();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleReviewSubmitted = () => {
    setShowReviewForm(null);
    if (selectedRequest) {
      loadRequestDetail(selectedRequest._id);
    }
    loadRequests();
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/services')} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
              <ChevronLeft size={18} /> Marketplace
            </button>
            <h1 className="font-bold text-gray-900">My Service Requests</h1>
          </div>
          <button
            onClick={() => navigate('/services/request')}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#1F3A68] text-white text-sm font-medium rounded-lg hover:bg-[#162d52]"
          >
            <Plus size={16} /> New Request
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { value: '', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'quoted', label: 'Quoted' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'completed', label: 'Completed' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setSelectedRequest(null); }}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                filter === f.value ? 'bg-[#1F3A68] text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1F3A68]" size={32} />
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Request List */}
            <div className={`space-y-3 ${selectedRequest ? 'w-2/5' : 'w-full'}`}>
              {requests.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border">
                  <FileText size={40} className="mx-auto mb-3 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-600">No service requests yet</h3>
                  <p className="text-gray-400 mt-1">Post a request to get quotes from local providers</p>
                  <button
                    onClick={() => navigate('/services/request')}
                    className="mt-4 px-6 py-2 bg-[#1F3A68] text-white rounded-lg hover:bg-[#162d52]"
                  >
                    Post a Service Request
                  </button>
                </div>
              ) : (
                requests.map(req => {
                  const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;
                  const isSelected = selectedRequest?._id === req._id;
                  return (
                    <div
                      key={req._id}
                      onClick={() => loadRequestDetail(req._id)}
                      className={`bg-white rounded-xl border p-4 cursor-pointer hover:shadow-sm transition-all ${
                        isSelected ? 'ring-2 ring-[#1F3A68] border-[#1F3A68]' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{req.title}</h3>
                          <p className="text-sm text-gray-500 capitalize mt-0.5">{(req.category || '').replace(/_/g, ' ')}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-2 line-clamp-2">{req.description}</p>
                      <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                        <span>{req.location?.postcode}</span>
                        <span className="flex items-center gap-1">
                          <MessageSquare size={12} />
                          {req.quotesCount || 0} quote{(req.quotesCount || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Detail Panel */}
            {selectedRequest && (
              <div className="w-3/5 bg-white rounded-xl border p-6 sticky top-20 self-start max-h-[80vh] overflow-y-auto">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-[#1F3A68]" size={24} />
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{selectedRequest.title}</h2>
                        <p className="text-sm text-gray-500 capitalize">{(selectedRequest.category || '').replace(/_/g, ' ')}</p>
                      </div>
                      <button onClick={() => setSelectedRequest(null)} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                      </button>
                    </div>

                    <p className="text-gray-600 mb-4">{selectedRequest.description}</p>

                    <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                      {selectedRequest.location?.postcode && (
                        <div className="bg-gray-50 rounded-lg p-2">
                          <span className="text-gray-400 block text-xs">Location</span>
                          <span className="font-medium">{selectedRequest.location.postcode}</span>
                        </div>
                      )}
                      {selectedRequest.preferredDate && (
                        <div className="bg-gray-50 rounded-lg p-2">
                          <span className="text-gray-400 block text-xs">Preferred Date</span>
                          <span className="font-medium">{new Date(selectedRequest.preferredDate).toLocaleDateString('en-GB')}</span>
                        </div>
                      )}
                      {(selectedRequest.budget?.min || selectedRequest.budget?.max) && (
                        <div className="bg-gray-50 rounded-lg p-2">
                          <span className="text-gray-400 block text-xs">Budget</span>
                          <span className="font-medium">
                            {selectedRequest.budget.min && `£${selectedRequest.budget.min}`}
                            {selectedRequest.budget.min && selectedRequest.budget.max && ' – '}
                            {selectedRequest.budget.max && `£${selectedRequest.budget.max}`}
                          </span>
                        </div>
                      )}
                      <div className="bg-gray-50 rounded-lg p-2">
                        <span className="text-gray-400 block text-xs">Status</span>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${(STATUS_CONFIG[selectedRequest.status] || STATUS_CONFIG.open).color}`}>
                          {(STATUS_CONFIG[selectedRequest.status] || STATUS_CONFIG.open).label}
                        </span>
                      </div>
                    </div>

                    {/* Quotes Section */}
                    {selectedRequest.quotes?.length > 0 && (
                      <div className="mt-4">
                        <h3 className="font-bold text-gray-900 mb-3">
                          {selectedRequest.quotes.length} Quote{selectedRequest.quotes.length !== 1 ? 's' : ''} Received
                        </h3>
                        <QuoteComparisonPanel
                          quotes={selectedRequest.quotes}
                          requestStatus={selectedRequest.status}
                          onAccept={(quoteId) => handleAcceptQuote(selectedRequest._id, quoteId)}
                        />
                      </div>
                    )}

                    {/* Payment CTA — appears once a quote is accepted but not
                        yet paid. Until the client pays, the provider does
                        the work at their own risk; this button starts the
                        Stripe Checkout flow so the platform takes payment
                        up front and pays the provider on completion. */}
                    {selectedRequest.status === 'accepted'
                      && selectedRequest.acceptedQuoteId
                      && selectedRequest.paymentStatus !== 'paid' && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-900 font-semibold mb-1">Quote accepted — secure your booking</p>
                        <p className="text-blue-700 text-sm mb-3">
                          Pay the accepted quote through Ariv. The provider receives
                          their share once the work is marked complete.
                        </p>
                        <button
                          onClick={() => handlePayForRequest(selectedRequest._id)}
                          className="px-4 py-2 bg-[#CB3B3B] text-white rounded-lg hover:bg-[#CB3B3B]/90 text-sm font-medium"
                        >
                          Pay for this service
                        </button>
                      </div>
                    )}

                    {selectedRequest.paymentStatus === 'paid' && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                        <strong>Paid.</strong> The provider will receive their share once the job is completed.
                      </div>
                    )}

                    {/* Review Prompt */}
                    {selectedRequest.status === 'completed' && selectedRequest.assignedProviderId && !showReviewForm && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-amber-800 font-medium mb-2">Job Complete — Leave a Review</p>
                        <p className="text-amber-600 text-sm mb-3">Share your experience to help other users find great providers.</p>
                        <button
                          onClick={() => setShowReviewForm(selectedRequest)}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium flex items-center gap-1.5"
                        >
                          <Star size={14} /> Write a Review
                        </button>
                      </div>
                    )}

                    {/* Review Form */}
                    {showReviewForm && showReviewForm._id === selectedRequest._id && (
                      <div className="mt-4">
                        <ProviderReviewForm
                          providerId={selectedRequest.assignedProviderId}
                          jobId={selectedRequest._id}
                          jobType="service_request"
                          onSubmitted={handleReviewSubmitted}
                          onCancel={() => setShowReviewForm(null)}
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-6 pt-4 border-t flex gap-2">
                      {['open', 'quoted'].includes(selectedRequest.status) && (
                        <button
                          onClick={() => handleCancel(selectedRequest._id)}
                          className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          Cancel Request
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServiceRequestsPage;
